import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  HomeIcon,
  ArrowRightOnRectangleIcon,
  ChartBarIcon,
  TagIcon,
  ChatBubbleLeftRightIcon,
  CalendarIcon,
  ShoppingCartIcon,
  UserGroupIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  BookOpenIcon,
  FolderIcon,
  QuestionMarkCircleIcon,
  PhotoIcon,
  UserIcon,
  CogIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';

const Sidebar: React.FC = () => {
  const location = useLocation();
  const [expandedSections, setExpandedSections] = useState<string[]>(['event', 'content', 'user']);

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const toggleSection = (section: string) => {
    setExpandedSections(prev => 
      prev.includes(section) 
        ? prev.filter(s => s !== section)
        : [...prev, section]
    );
  };

  const isSectionExpanded = (section: string) => expandedSections.includes(section);

  const navItems = [
    { path: '/', name: 'Overview', icon: HomeIcon },
    { path: '/login', name: 'Login', icon: ArrowRightOnRectangleIcon },
    { path: '/dashboard', name: 'Dashboard', icon: ChartBarIcon },
    { path: '/brands', name: 'Brands', icon: TagIcon },
    { path: '/messages', name: 'Messages', icon: ChatBubbleLeftRightIcon },
    { path: '/testimonials', name: 'Testimonials', icon: ChatBubbleLeftRightIcon },
  ];

  const eventManagementItems = [
    { path: '/events', name: 'Events', icon: CalendarIcon },
    { path: '/orders', name: 'Orders', icon: ShoppingCartIcon },
    { path: '/participants', name: 'Participants', icon: UserGroupIcon },
    { path: '/transactions', name: 'Transactions', icon: CurrencyDollarIcon },
    { path: '/transaction-logs', name: 'Transaction Logs', icon: DocumentTextIcon },
  ];

  const contentItems = [
    { path: '/pages', name: 'Pages', icon: DocumentTextIcon },
    { path: '/posts', name: 'Posts', icon: BookOpenIcon },
    { path: '/resource-types', name: 'Resource Types', icon: FolderIcon },
    { path: '/resources', name: 'Resources', icon: FolderIcon },
    { path: '/faq', name: 'FAQ', icon: QuestionMarkCircleIcon },
    { path: '/media', name: 'Media', icon: PhotoIcon },
  ];

  const userManagementItems = [
    { path: '/users', name: 'Users', icon: UserIcon },
    { path: '/teams', name: 'Teams', icon: UserGroupIcon },
    { path: '/members', name: 'Members', icon: UserIcon },
  ];

  return (
    <aside className="sidebar scrollbar-custom">
      <div className="sidebar-content">
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <DocumentTextIcon className="text-blue-400" />
            <h1 className="sidebar-title">Dashboard Docs</h1>
          </div>
        </div>

        <nav className="flex-1">
          <ul className="space-y-1">
            {navItems.map((item) => (
              <li key={item.path}>
                <Link
                  to={item.path}
                  className={`nav-link ${isActive(item.path) ? 'active' : ''}`}
                >
                  <item.icon />
                  {item.name}
                </Link>
              </li>
            ))}

            <li className="nav-section">
              <div 
                className={`nav-section-header ${isSectionExpanded('event') ? 'expanded' : ''}`}
                onClick={() => toggleSection('event')}
              >
                <span>Event Management</span>
                <ChevronRightIcon />
              </div>
              <div className={`nav-section-content ${isSectionExpanded('event') ? 'expanded' : ''}`}>
                <ul className="sub-nav">
                  {eventManagementItems.map((item) => (
                    <li key={item.path}>
                      <Link
                        to={item.path}
                        className={`nav-link ${isActive(item.path) ? 'active' : ''}`}
                      >
                        <item.icon />
                        {item.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </li>

            <li className="nav-section">
              <div 
                className={`nav-section-header ${isSectionExpanded('content') ? 'expanded' : ''}`}
                onClick={() => toggleSection('content')}
              >
                <span>Content</span>
                <ChevronRightIcon />
              </div>
              <div className={`nav-section-content ${isSectionExpanded('content') ? 'expanded' : ''}`}>
                <ul className="sub-nav">
                  {contentItems.map((item) => (
                    <li key={item.path}>
                      <Link
                        to={item.path}
                        className={`nav-link ${isActive(item.path) ? 'active' : ''}`}
                      >
                        <item.icon />
                        {item.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </li>

            <li>
              <Link
                to="/consultations"
                className={`nav-link ${isActive('/consultations') ? 'active' : ''}`}
              >
                <CalendarIcon />
                Consultations
              </Link>
            </li>

            <li className="nav-section">
              <div 
                className={`nav-section-header ${isSectionExpanded('user') ? 'expanded' : ''}`}
                onClick={() => toggleSection('user')}
              >
                <span>User Management</span>
                <ChevronRightIcon />
              </div>
              <div className={`nav-section-content ${isSectionExpanded('user') ? 'expanded' : ''}`}>
                <ul className="sub-nav">
                  {userManagementItems.map((item) => (
                    <li key={item.path}>
                      <Link
                        to={item.path}
                        className={`nav-link ${isActive(item.path) ? 'active' : ''}`}
                      >
                        <item.icon />
                        {item.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </li>

            <li>
              <Link
                to="/settings"
                className={`nav-link ${isActive('/settings') ? 'active' : ''}`}
              >
                <CogIcon />
                Settings
              </Link>
            </li>
          </ul>
        </nav>
      </div>
    </aside>
  );
};

export default Sidebar; 